import type { ComponentType, FC } from 'react';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { Link, matchPath, useHistory, useLocation } from 'react-router-dom';

import { useOidc as useReactOidc } from '@axa-fr/react-oidc';
import type { AppBarProps } from '@mui/material';
import { AppBar, Box, Divider, IconButton, Menu, MenuItem, Stack, Toolbar, Tooltip, Typography } from '@mui/material';

import { trackEvent } from 'analytics/appInsights';
import { events } from 'analytics/events';
import { useFeatureFlag } from 'api/configuration/features/hooks/useFeatureFlag';
import { useGetCurrentProposal } from 'api/projectVersioning/hooks/useGetCurrentProposal';
import { useFetchCurrentSubscription } from 'api/subscriptions/hooks/useFetchCurrentSubscription';
import { useFetchTemplate } from 'api/templates/hooks/useFetchTemplate';
import { ENV_VARS } from 'app/config/environment-vars';
import { WhenFeatureIsEnabled } from 'app/configuration/WhenFeatureIsEnabled';
import { projectPaths } from 'app/routes/paths/project';
import { ReactComponent as Logo } from 'assets/logo.svg';
import { WhenUserHasScope } from 'components/auth/WhenUserHasScope';
import { ConditionalWrapper } from 'components/data-display/ConditionalWrapper';
import { UserAvatar } from 'components/data-display/avatar/UserAvatar';
import LockIcon from 'components/icons/Lock';
import AppsIcon from 'components/icons/custom/Apps';
import { BaseDialog } from 'components/surfaces/BaseDialog/BaseDialog';
import { UserPermissions } from 'components/user-profile/UserPermissions';
import { UserScopes } from 'components/user-profile/UserScopes';
import useSetDocumentTitle from 'hooks/useSetDocumentTitle';
import { useUserInfo, useUserWorksAtLevvr } from 'state/appState/appStateHooks';
import { PROJECTS_STORE } from 'state/projects/projectsStore';
import { useUserHasScope, useUserIsOwner } from 'state/userState/userState';
import { isProduction } from 'utils/env';

export interface TopBarProps extends AppBarProps {
  ProposalContainer: ComponentType<{
    title: string | undefined;
    navigateTo: (path: string, options?: { replace?: boolean }) => void;
  }>;
  FeatureFlagsMenu: ComponentType;
  FullTextSearchBar: ComponentType;
}

/**
 * Area titles for the browser tab and the app bar. `/` is the catch-all and must stay last —
 * lookup is a substring match, and every pathname contains `/`. `hasPageHeader` marks areas whose
 * pages render their own PageHeader carrying this same title, so the app bar suppresses its copy
 * to avoid showing it twice (the browser tab title is unaffected).
 */
const AREA_TITLES = [
  { path: '/projects', title: 'Projects' },
  { path: '/templates', title: 'Blueprints' },
  { path: '/libraries', title: 'Libraries' },
  { path: '/subscription', title: 'Subscription management', hasPageHeader: true },
  { path: '/integrations', title: 'Integrations' },
  { path: '/personal-settings', title: 'User preferences', hasPageHeader: true },
  { path: '/', title: 'Projects' }
];

export const TopBar: FC<TopBarProps> = ({
  children,
  ProposalContainer,
  FeatureFlagsMenu,
  FullTextSearchBar,
  ...other
}) => {
  const projects = PROJECTS_STORE((state) => state.projects);
  const isProjectVersioningEnabled = useFeatureFlag('ProjectVersioning');
  const { data: subscription } = useFetchCurrentSubscription();
  const { data: currentProposal } = useGetCurrentProposal();

  const currentProposalName = currentProposal?.name || '';
  const subscriptionName = subscription?.name || '';

  const { logout } = useReactOidc();

  const history = useHistory();
  const location = useLocation();

  const userInfo = useUserInfo();
  const userIsOwner = useUserIsOwner();

  const authUrl = ENV_VARS.IDENTITY_URL;
  const returnUrl = ENV_VARS.REDIRECT_URL;

  const matchProjectsRoute = matchPath(location.pathname, { path: ['/projects', '/'], exact: true });
  const matchProjectRoute = matchPath<{ projectId: string }>(location.pathname, { path: [`${projectPaths.BASE}`] });
  const matchTemplatesRoute = matchPath(location.pathname, { path: '/templates', exact: true });
  const matchTemplateRoute = matchPath(location.pathname, { path: '/templates/:templateId/*' });
  const matchLibrariesRoute = matchPath(location.pathname, { path: '/libraries', exact: true });
  const matchLibraryRoute = matchPath(location.pathname, { path: '/libraries/:templateId/*' });
  const matchIntegrationsRoute = matchPath(location.pathname, { path: '/integrations' });
  const matchSubscriptionRoute = matchPath(location.pathname, { path: '/subscription' });

  const projectId = matchProjectRoute?.params?.projectId;
  const templateId =
    (matchTemplateRoute?.params as { templateId?: string })?.templateId ||
    (matchLibraryRoute?.params as { templateId?: string })?.templateId;

  const { data: template } = useFetchTemplate(templateId, (data) => ({
    technicalName: data.technicalName,
    locked: !!data.releasedOn
  }));

  const [userAnchorEl, setUserAnchorEl] = useState<HTMLElement | null>(null);
  const [switchAnchorEl, setSwitchAnchorEl] = useState<HTMLElement | null>(null);

  const [isPermissionsDialogOpen, setIsPermissionsDialogOpen] = useState(false);
  const [isScopesDialogOpen, setIsScopesDialogOpen] = useState(false);

  const hasTemplateCreatorScope = useUserHasScope('template:creator');
  const hasIntegrationsOwnerScope = useUserHasScope('integrations:owner');
  const isOwner = useUserIsOwner();

  const userWorksAtLevvr = useUserWorksAtLevvr();

  const userIsTemplateCreator = isOwner || hasTemplateCreatorScope;
  const userIsIntegrationsOwner = isOwner || hasIntegrationsOwnerScope;

  const matchedArea = useMemo(() => AREA_TITLES.find((r) => location.pathname.includes(r.path)), [location.pathname]);

  const areaTitle = useMemo(() => {
    const defaultTitle = subscriptionName;
    if (matchProjectRoute) {
      const project = projects.find((p) => p.id === projectId);

      if (project !== undefined) {
        return project.name;
      }

      return defaultTitle;
    }

    if (matchTemplateRoute || matchLibraryRoute) {
      return template?.technicalName;
    }

    return matchedArea?.title;
  }, [
    matchProjectRoute,
    matchTemplateRoute,
    matchLibraryRoute,
    projects,
    projectId,
    template,
    matchedArea,
    subscriptionName
  ]);

  // Areas that carry their own PageHeader state their identity in the page, so repeating it here
  // would show the same title twice on screen. The browser tab still gets the area title.
  const title = useMemo(() => (matchedArea?.hasPageHeader ? undefined : areaTitle), [matchedArea, areaTitle]);

  useSetDocumentTitle(areaTitle, subscriptionName, currentProposalName);

  const onAccountInformation = () => {
    window.location.href = `${authUrl}/Identity/Account/Manage`;
  };

  const onSwitchSubscription = () => {
    window.sessionStorage.clear(); // hacky method for clearing the auth
    window.localStorage.clear();

    window.location.href = `${authUrl}/tenant?returnUrl=${returnUrl!.replace('/callback', '')}`;
  };

  const onPermissionsDialogOpen = useCallback(() => {
    setIsPermissionsDialogOpen(!isPermissionsDialogOpen);
    setUserAnchorEl(null);
  }, [isPermissionsDialogOpen]);

  const onScopesDialogOpen = useCallback(() => {
    setIsScopesDialogOpen(!isScopesDialogOpen);
    setUserAnchorEl(null);
  }, [isScopesDialogOpen]);

  const onNavigateTo = useCallback(
    (path: string, options?: { replace?: boolean }) => {
      setSwitchAnchorEl(null);
      setUserAnchorEl(null);

      if (options?.replace) {
        history.replace(path);
      } else {
        history.push(path);
      }
    },
    [history]
  );

  const homeUrl = matchProjectRoute ? '/projects' : matchTemplateRoute ? '/templates' : '/';

  const onHomeClick = useCallback(() => {
    trackEvent(events.common.topbar.home, 'Common', { url: homeUrl });
  }, [homeUrl]);

  const onAppsClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    trackEvent(events.common.topbar['app-areas'], 'Common');
    setSwitchAnchorEl(event.currentTarget);
  }, []);

  const onAccountClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    trackEvent(events.common.topbar.account, 'Common');
    setUserAnchorEl(event.currentTarget);
  }, []);

  return (
    <AppBar
      position='sticky'
      // elevation={0} because AppBar defaults to 4; the bottom divider replaces that shadow. Without
      // a divider there would be nothing at all separating the bar from the page, since both are
      // steel[50].
      elevation={0}
      sx={(theme) => ({
        backgroundColor: theme.palette.colors.steel[50],
        borderBottom: `1px solid ${theme.palette.divider}`
      })}
      enableColorOnDark
      {...other}
    >
      <Toolbar variant='dense'>
        <Link to={homeUrl} onClick={onHomeClick}>
          <Box height={20} width={20}>
            <Logo height={20} width={20} preserveAspectRatio='xMaxYMin meet' />
          </Box>
        </Link>

        {matchProjectRoute && isProjectVersioningEnabled ? (
          <ProposalContainer title={title} navigateTo={onNavigateTo} />
        ) : (
          title && (
            <Typography variant='h6' flex='0 1 auto' ml={2} noWrap id='area-title' color='text.primary'>
              {title}
            </Typography>
          )
        )}

        {!!matchTemplateRoute && template?.locked && (
          <Tooltip title='This blueprint is locked'>
            <span>
              <LockIcon
                fontSize='tiny'
                sx={(theme) => ({
                  verticalAlign: 'middle',
                  color: theme.palette.colors.steel[900],
                  marginLeft: 1
                })}
              />
            </span>
          </Tooltip>
        )}

        {matchProjectRoute && isProjectVersioningEnabled ? null : (
          // Without an area title this is the only text in the bar, so it takes over its indent.
          <Typography
            variant='h6'
            flex='0 1 auto'
            ml={title ? 1 : 2}
            mr={1}
            noWrap
            id='subscription-title'
            color='text.primary'
          >
            {title && subscriptionName ? ' | ' : ''}
            {subscriptionName}
          </Typography>
        )}
        <Box display='flex' flexDirection='row' alignItems='center' justifyContent='flex-end' flex={1} mx={1}>
          {children}
        </Box>

        <Stack direction='row' gap={1}>
          {matchProjectRoute && !currentProposal && <FullTextSearchBar />}
          {!isProduction && userWorksAtLevvr && <FeatureFlagsMenu />}

          <Tooltip title='Switch to...' placement='bottom'>
            <IconButton onClick={onAppsClick}>
              <AppsIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={userInfo.fullName} placement='bottom-end'>
            <IconButton onClick={onAccountClick}>
              <UserAvatar value={userInfo.initials} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Toolbar>

      <Menu
        onClose={() => setUserAnchorEl(null)}
        anchorEl={userAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slotProps={{
          paper: {
            sx: { minWidth: 160 }
          }
        }}
        open={!!userAnchorEl}
        keepMounted
      >
        {userIsOwner && <MenuItem onClick={onPermissionsDialogOpen}>Permissions</MenuItem>}
        {userIsOwner && <MenuItem onClick={onScopesDialogOpen}>Scopes</MenuItem>}

        <MenuItem onClick={onAccountInformation}>Manage account</MenuItem>

        <MenuItem onClick={() => onNavigateTo('/personal-settings')}>Personal settings</MenuItem>

        <MenuItem onClick={onSwitchSubscription}>Switch subscription</MenuItem>

        <Divider />

        <MenuItem onClick={() => logout()}>Log out</MenuItem>
      </Menu>

      <Menu
        onClose={() => setSwitchAnchorEl(null)}
        anchorEl={switchAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slotProps={{
          paper: {
            sx: { minWidth: 160 }
          }
        }}
        open={!!switchAnchorEl}
        keepMounted
      >
        <MenuItem selected={!!(matchProjectRoute || matchProjectsRoute)} onClick={() => onNavigateTo('/projects')}>
          Projects
        </MenuItem>

        <ConditionalWrapper
          condition={!userIsTemplateCreator}
          wrapper={(children) => (
            <Tooltip title='You are not authorized to manage blueprints'>
              <span>{children}</span>
            </Tooltip>
          )}
        >
          <MenuItem
            selected={!!(matchTemplateRoute || matchTemplatesRoute)}
            disabled={!userIsTemplateCreator}
            onClick={() => onNavigateTo('/templates')}
          >
            Blueprints
          </MenuItem>
        </ConditionalWrapper>

        <WhenFeatureIsEnabled featureName='KnowledgeManagement'>
          <ConditionalWrapper
            condition={!userIsTemplateCreator}
            wrapper={(children) => (
              <Tooltip title='You are not authorized to manage libraries'>
                <span>{children}</span>
              </Tooltip>
            )}
          >
            <MenuItem
              selected={!!(matchLibraryRoute || matchLibrariesRoute)}
              disabled={!userIsTemplateCreator}
              onClick={() => onNavigateTo('/libraries')}
            >
              Libraries
            </MenuItem>
          </ConditionalWrapper>
        </WhenFeatureIsEnabled>

        <WhenFeatureIsEnabled featureName='Integrations'>
          <ConditionalWrapper
            condition={!userIsIntegrationsOwner}
            wrapper={(children) => (
              <Tooltip title='You are not authorized to manage integrations'>
                <span>{children}</span>
              </Tooltip>
            )}
          >
            <MenuItem
              selected={!!matchIntegrationsRoute}
              disabled={!userIsIntegrationsOwner}
              onClick={() => onNavigateTo('/integrations')}
            >
              Integrations
            </MenuItem>
          </ConditionalWrapper>
        </WhenFeatureIsEnabled>

        <WhenUserHasScope scope='subscription:owner'>
          <Divider />

          <MenuItem selected={!!matchSubscriptionRoute} onClick={() => onNavigateTo('/subscription/users')}>
            Subscription management
          </MenuItem>
        </WhenUserHasScope>
      </Menu>

      <BaseDialog
        actions={{ cancel: { onClick: () => setIsPermissionsDialogOpen(!isPermissionsDialogOpen), title: 'Close' } }}
        title='User permissions: enabled features'
        open={isPermissionsDialogOpen}
        maxWidth='md'
        fullWidth
        hideTitleClose
      >
        <UserPermissions />
      </BaseDialog>

      <BaseDialog
        actions={{ cancel: { onClick: () => setIsScopesDialogOpen(!isScopesDialogOpen), title: 'Close' } }}
        title='User token information'
        open={isScopesDialogOpen}
        maxWidth='md'
        fullWidth
        hideTitleClose
      >
        <UserScopes />
      </BaseDialog>
    </AppBar>
  );
};
